import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Trash2, Edit, Loader2, X, Save, Upload } from 'lucide-react';
import { categoriesAPI, uploadAPI } from '../../services/api';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    image: ''
  });

  useEffect(() => {
    let isMounted = true;
    
    const fetchCategories = async () => {
      try {
        const response = await categoriesAPI.getAll();
        if (isMounted) {
          setCategories(response.data || []);
        }
      } catch (error) {
        if (isMounted && error.name !== 'CanceledError') {
          console.error('Error fetching categories:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchCategories();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name || '',
        slug: category.slug || '',
        description: category.description || '',
        image: category.image || ''
      });
    } else {
      setEditingCategory(null);
      setFormData({ name: '', slug: '', description: '', image: '' });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        const response = await categoriesAPI.update(editingCategory._id, formData);
        setCategories(categories.map(c => c._id === editingCategory._id ? response.data : c));
        alert('Category updated!');
      } else {
        const response = await categoriesAPI.create(formData);
        setCategories([...categories, response.data]);
        alert('Category created!');
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error saving category:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save category';
      alert(errorMessage);
    }
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('Delete this category?')) {
      try {
        await categoriesAPI.delete(categoryId);
        setCategories(categories.filter(c => c._id !== categoryId));
        alert('Category deleted!');
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Failed to delete category');
      }
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      setFormData({ ...formData, image: response.data.url });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-charcoal font-heading">Categories</h1>
            <p className="text-stone-grey font-body">{categories.length} total categories</p>
          </div>
          <Button 
            className="bg-[#2C2824] hover:bg-[#1a1816] text-white font-body"
            onClick={() => handleOpenModal()}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Category
          </Button>
        </div>

        <div className="bg-white rounded-lg border border-soft-sand shadow-soft overflow-hidden">
          <table className="w-full">
            <thead className="bg-soft-sand">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Image</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Slug</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Products</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-charcoal font-body">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-soft-sand">
              {categories.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-stone-grey font-body">
                    No categories yet. Click &quot;Add Category&quot; to create one.
                  </td>
                </tr>
              ) : (
                categories.map((category) => (
                  <tr key={category._id} className="hover:bg-soft-sand/30 transition-colors">
                    <td className="px-6 py-4">
                      {category.image ? (
                        <img src={category.image} alt={category.name} className="w-12 h-12 object-cover rounded" />
                      ) : (
                        <div className="w-12 h-12 bg-soft-sand rounded flex items-center justify-center text-stone-grey text-xs">
                          No img
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 font-medium text-charcoal font-body">{category.name}</td>
                    <td className="px-6 py-4 text-sm text-stone-grey font-body">{category.slug}</td>
                    <td className="px-6 py-4 text-sm text-stone-grey font-body">{category.product_count || 0}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-charcoal hover:bg-soft-sand"
                          onClick={() => handleOpenModal(category)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-soft-terracotta hover:bg-soft-terracotta/10" 
                          onClick={() => handleDelete(category._id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
            <div className="p-6 border-b border-soft-sand flex items-center justify-between">
              <h2 className="text-2xl font-bold text-charcoal font-heading">
                {editingCategory ? 'Edit Category' : 'Add Category'}
              </h2>
              <button onClick={handleCloseModal} className="text-stone-grey hover:text-charcoal">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1 font-body">Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    name: e.target.value,
                    slug: editingCategory ? formData.slug : generateSlug(e.target.value)
                  })}
                  placeholder="Category name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1 font-body">Slug</label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="category-slug"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2 font-body">Image</label>
                
                {formData.image && (
                  <div className="mb-3 p-3 bg-soft-sand/30 rounded-lg flex items-center gap-3">
                    <img src={formData.image} alt="Preview" className="w-16 h-16 object-cover rounded" />
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="icon"
                      onClick={() => setFormData({ ...formData, image: '' })}
                      className="text-red-500 hover:bg-red-50 ml-auto"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                
                <label className="block">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                  <div className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-soft-sand rounded-lg cursor-pointer hover:border-muted-green hover:bg-soft-sand/20 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                    {uploading ? (
                      <Loader2 className="h-5 w-5 animate-spin text-muted-green" />
                    ) : (
                      <Upload className="h-5 w-5 text-stone-grey" />
                    )}
                    <span className="font-body text-stone-grey">
                      {uploading ? 'Uploading...' : 'Upload Image'}
                    </span>
                  </div>
                </label>
                
                <div className="mt-2">
                  <Input
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    placeholder="Or paste image URL"
                    className="text-sm"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1 font-body">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Category description..."
                  rows={3}
                  className="w-full px-3 py-2 border border-soft-sand rounded-md font-body"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseModal} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-muted-green hover:bg-muted-green/90 text-white">
                  <Save className="h-4 w-4 mr-2" />
                  {editingCategory ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCategories;
